import { ContentContainer } from '@/components/ContentContainer';
import { CustomProviderModal } from '@/components/CustomProviderModal';
import { IconPlus } from '@/components/icons/IconPlus';
import { PrimaryButton } from '@/components/PrimaryButton';
import { StepForm } from '@/components/StepForm';
import { StepSubtitle } from '@/components/StepSubtitle';
import { StepTitle } from '@/components/StepTitle';
import { billAmountContent } from '@/content/billAmountContent';
import { staticStepRoute } from '@/router/router';
import { BillAmountProvider } from '@/state/state.types';
import { ActionIcon, Flex, Text } from '@mantine/core';
import { useParams } from '@tanstack/router';
import { SyntheticEvent, useCallback, useMemo, useState } from 'react';
import { BillAmountRowItem } from './BillAmountRowItem';
import { useBillAmountForm } from './hooks/useBillAmountForm';
import { useCaseUpdater } from './hooks/useCaseUpdater';

type ModalState = {
  open: boolean;
  rowKey: string | null;
  initialValues?: BillAmountProvider;
};

const INITIAL_MODAL_STATE: ModalState = { open: false, rowKey: null };

export const BillAmount = () => {
  const { stepId } = useParams({ from: staticStepRoute.id }) as { stepId: string };

  if (!stepId) throw new Error('StepId should be defined');

  const {
    rows,
    addRow,
    removeRow,
    updateAmount,
    updateProvider,
    updateCustomProvider,
    clearProvider,
    validateRow,
    validate,
    formTransformer,
    rowHasData,
  } = useBillAmountForm();

  const [modalState, setModalState] = useState<ModalState>(INITIAL_MODAL_STATE);

  const memoizedFormTransformer = useMemo(() => formTransformer(stepId), [formTransformer, stepId]);

  const { onFormSubmit, isLoading } = useCaseUpdater({
    formTransformer: memoizedFormTransformer,
  });

  const handleSubmit = useCallback(
    (e: SyntheticEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (!validate()) return;
      onFormSubmit(e);
    },
    [validate, onFormSubmit],
  );

  const handleModalSave = useCallback(
    (provider: BillAmountProvider) => {
      if (modalState.rowKey) {
        updateCustomProvider(modalState.rowKey, provider);
      }
      setModalState(INITIAL_MODAL_STATE);
    },
    [modalState.rowKey, updateCustomProvider],
  );

  const handleModalClose = useCallback(() => {
    setModalState(INITIAL_MODAL_STATE);
  }, []);

  const handleAmountChange = useCallback(
    (key: string, value: string) => {
      updateAmount(key, value);
    },
    [updateAmount],
  );

  const handleAmountBlur = useCallback(
    (key: string) => {
      validateRow(key, 'amount');
    },
    [validateRow],
  );

  const handleProviderSelect = useCallback(
    (key: string, provider: BillAmountProvider) => {
      updateProvider(key, provider);
    },
    [updateProvider],
  );

  const handleProviderClear = useCallback(
    (key: string) => {
      clearProvider(key);
    },
    [clearProvider],
  );

  const handleCustomProviderClick = useCallback(
    (key: string, initialValues?: BillAmountProvider) => {
      const row = rows.find((r) => r.key === key);
      if (row?.isCustomProvider && row.provider) {
        setModalState({ open: true, rowKey: key, initialValues: row.provider });
      } else {
        setModalState({ open: true, rowKey: key, initialValues });
      }
    },
    [rows],
  );

  const handleProviderBlur = useCallback(
    (key: string) => {
      validateRow(key, 'provider');
    },
    [validateRow],
  );

  const handleRemove = useCallback(
    (key: string) => {
      removeRow(key);
    },
    [removeRow],
  );

  return (
    <Flex
      direction="column"
      h="100%"
    >
      <StepTitle fz={{ base: 28, md: 32 }}>{billAmountContent.title}</StepTitle>
      <StepSubtitle>{billAmountContent.subtitle}</StepSubtitle>
      <ContentContainer>
        <StepForm
          onSubmit={handleSubmit}
          style={{ flex: 1, display: 'flex', flexDirection: 'column' }}
        >
          <input
            name="stepId"
            type="hidden"
            value={stepId}
          />
          <Flex
            direction="column"
            gap="md"
            mt={24}
            sx={{ flex: 1 }}
          >
            {rows.map((row) => (
              <BillAmountRowItem
                key={row.key}
                row={row}
                onAmountChange={handleAmountChange}
                onAmountBlur={handleAmountBlur}
                onProviderSelect={handleProviderSelect}
                onProviderClear={handleProviderClear}
                onCustomProviderClick={handleCustomProviderClick}
                onProviderBlur={handleProviderBlur}
                onRemove={handleRemove}
                showRemove={rows.length > 1 && rowHasData(row)}
                isLoading={isLoading}
              />
            ))}
            <Flex
              justify="center"
              mt={8}
              mr={44}
            >
              <ActionIcon
                variant="outline"
                onClick={addRow}
                radius="xl"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  width: 'auto',
                  height: 'auto',
                  padding: '8px 24px',
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                }}
              >
                <IconPlus />
                <Text
                  fw={700}
                  c="primary.0"
                  size="sm"
                >
                  {billAmountContent.addAnotherLabel}
                </Text>
              </ActionIcon>
            </Flex>
          </Flex>
          <Flex
            justify="center"
            mr={44}
          >
            <PrimaryButton
              isLoading={isLoading}
              isDisabled={isLoading}
              mt={24}
              align="center"
              mx="auto"
              maw={343}
              w="100%"
            >
              {billAmountContent.continueLabel}
            </PrimaryButton>
          </Flex>
        </StepForm>
      </ContentContainer>
      <CustomProviderModal
        opened={modalState.open}
        onClose={handleModalClose}
        onSave={handleModalSave}
        initialValues={modalState.initialValues}
      />
    </Flex>
  );
};
